/**
 * Ambient Audio Module — Continuous Background Atmosphere
 *
 * Purpose:
 * - Looping ambient audio across the entire scrollytelling experience
 * - Runs OUTSIDE MediaManager (not affected by single-medium enforcement)
 * - Volume ducking when other media plays (videos, audio bubbles, etc.)
 * - User-controllable mute toggle (speaker icon, top-left)
 *
 * Configuration (via Code Injection or data attribute):
 *   window.storyMeta.ambientSrc = 'https://...mp3';
 *   window.storyMeta.ambientVolume = 0.4;       // Base volume (0-1, default 0.4)
 *   window.storyMeta.ambientDuckVolume = 0.1;    // Ducked volume (default 0.1)
 *
 * Or via data attribute on story container:
 *   data-ambient-src="https://...mp3"
 *
 * Lifecycle:
 *   1. Waits for user gesture (Start button via StoryLoader)
 *   2. Starts playing with fade-in
 *   3. Ducks volume when MediaManager dispatches story:media:ducking
 *   4. Restores volume when story:media:unducking
 *   5. User can mute/unmute via toggle button
 *
 * File: assets/js/story/story-module-ambient.js
 */

(function (window) {
  'use strict';

  var DEBUG = window.location && window.location.search.indexOf('debug=story') !== -1;

  function log() {
    if (DEBUG && window.console) {
      var args = ['[Ambient]'].concat(Array.prototype.slice.call(arguments));
      console.log.apply(console, args);
    }
  }

  /* ========================================
     Constructor
     ======================================== */

  function StoryAmbientAudio() {
    // Audio element
    this.audio = null;
    this.audioSrc = '';

    // Volume settings
    this.baseVolume = 0.4;
    this.duckVolume = 0.1;
    this.currentTargetVolume = 0;

    // State
    this.isPlaying = false;
    this.isMuted = false;
    this.isDucked = false;
    this.started = false;

    // Fade interval
    this._fadeInterval = null;

    // UI
    this.toggleBtn = null;

    // Don't auto-init — wait for explicit start call
    log('Module created, waiting for start()');
  }

  /* ========================================
     Initialize (called from story-init.js)
     ======================================== */

  StoryAmbientAudio.prototype.init = function () {
    // Resolve audio source
    this.audioSrc = this._resolveSource();
    if (!this.audioSrc) {
      log('No ambient audio source configured — module inactive');
      return;
    }

    // Resolve volume settings
    var meta = window.storyMeta || {};
    if (typeof meta.ambientVolume === 'number') {
      this.baseVolume = meta.ambientVolume;
    }
    if (typeof meta.ambientDuckVolume === 'number') {
      this.duckVolume = meta.ambientDuckVolume;
    }

    // Create audio element
    this.audio = new Audio();
    this.audio.loop = true;
    this.audio.preload = 'auto';
    this.audio.volume = 0;
    this.audio.src = this.audioSrc;

    // Blob-Vorladen (Live-Befund 2026-07-24): auf dünnen Leitungen stockte
    // der Atmo-Stream — 200 s bis zum ersten Ton, 'waiting' an jeder
    // Loop-Grenze, Rebuffering bei Panelwechseln (Bandbreiten-Konkurrenz
    // mit Pano-Kacheln/Bildern) → klang wie „lädt jedes Mal neu". Die Datei
    // ist klein (~1–2 MB): einmal komplett holen und aus dem Speicher
    // loopen. Der Stream-Start bleibt bestehen (Gesten-Kontext!); sobald
    // der Blob da ist, wird positionserhaltend umgehängt.
    var self = this;
    if (window.fetch) {
      fetch(this.audioSrc).then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.blob();
      }).then(function (blob) {
        self._swapToBlob(URL.createObjectURL(blob));
      }).catch(function (err) {
        log('Blob-Vorladen fehlgeschlagen — bleibe beim Stream:', err && err.message);
      });
    }

    // Create toggle button
    this._createToggleUI();

    // Listen for ducking events from MediaManager
    var self = this;
    window.addEventListener('story:media:ducking', function () {
      self._onDuck();
    });
    window.addEventListener('story:media:unducking', function () {
      self._onUnduck();
    });

    // Listen for story start (loader fires this on Start click)
    window.addEventListener('story:started', function () {
      self.start();
    });

    // Fallback: if story already started (loader already done), start now
    if (window.StoryMediaManager && window.StoryMediaManager.userGestureReceived) {
      this.start();
    }

    log('Initialized — src:', this.audioSrc, 'vol:', this.baseVolume);
  };

  /* ========================================
     Resolve Audio Source
     ======================================== */

  StoryAmbientAudio.prototype._resolveSource = function () {
    // Priority 1: window.storyMeta
    var meta = window.storyMeta || {};
    if (meta.ambientSrc) return meta.ambientSrc;

    // Priority 2: data attribute on story container
    var container = document.querySelector('[data-story-template]');
    if (container && container.dataset.ambientSrc) {
      return container.dataset.ambientSrc;
    }

    return '';
  };

  /* ========================================
     Start Playback
     ======================================== */

  StoryAmbientAudio.prototype.start = function () {
    if (this.started || !this.audio || !this.audioSrc) return;
    this.started = true;

    var self = this;
    this.currentTargetVolume = this.baseVolume;

    var playPromise = this.audio.play();
    if (playPromise !== undefined) {
      playPromise.then(function () {
        self.isPlaying = true;
        self._fadeTo(self.baseVolume, 800);
        self._showToggle();
        log('Playback started, fading to', self.baseVolume);
      }).catch(function (err) {
        log('Autoplay blocked:', err.message);
        // Retry on next user interaction
        document.addEventListener('click', function retry() {
          document.removeEventListener('click', retry);
          self.audio.play().then(function () {
            self.isPlaying = true;
            self._fadeTo(self.baseVolume, 800);
            self._showToggle();
          }).catch(function () {});
        }, { once: true });
      });
    }
  };

  /* ========================================
     Blob-Swap (nahtlos vom Stream auf die
     komplett geladene Datei umhängen)
     ======================================== */

  StoryAmbientAudio.prototype._swapToBlob = function (url) {
    var a = this.audio;
    if (!a) return;
    var t = a.currentTime || 0;
    var wasPlaying = this.started && !a.paused;
    a.src = url; // ab hier keine Netz-Abhängigkeit mehr (Loop lückenlos)
    a.loop = true;
    a.addEventListener('loadedmetadata', function once() {
      a.removeEventListener('loadedmetadata', once);
      try {
        a.currentTime = (a.duration && t < a.duration) ? t : 0;
      } catch (e) { /* Position ist Komfort, kein Muss */ }
      if (wasPlaying) {
        var p = a.play();
        if (p && p.catch) p.catch(function () {});
      }
    });
    log('Atmo auf Blob umgehängt (Position ' + Math.round(t) + 's, lief: ' + wasPlaying + ')');
  };

  /* ========================================
     Volume Ducking
     ======================================== */

  StoryAmbientAudio.prototype._onDuck = function () {
    if (!this.isPlaying || this.isMuted) return;
    this.isDucked = true;
    this.currentTargetVolume = this.duckVolume;
    this._fadeTo(this.duckVolume, 300);
    log('Ducking to', this.duckVolume);
  };

  StoryAmbientAudio.prototype._onUnduck = function () {
    if (!this.isPlaying || this.isMuted) return;
    this.isDucked = false;
    this.currentTargetVolume = this.baseVolume;
    this._fadeTo(this.baseVolume, 500);
    log('Unducking to', this.baseVolume);
  };

  /* ========================================
     Mute Toggle
     ======================================== */

  StoryAmbientAudio.prototype.toggleMute = function () {
    this.isMuted = !this.isMuted;
    var self = this;
    clearTimeout(this._muteDelay);

    // iOS-Safari ignoriert programmatisches audio.volume (Wert bleibt fest) →
    // der Volume-Fade allein stummt dort NICHT (B-2, 2026-07-25). Zusätzlich die
    // muted-PROPERTY setzen — die ist auf iOS zuverlässig skriptbar. Desktop
    // fadet weiter sanft; iOS schaltet über .muted hart, aber wirksam.
    if (this.isMuted) {
      this._fadeTo(0, 200);
      // Mute erst nach dem Fade, damit die Desktop-Ausblendung erhalten bleibt
      // (auf Desktop ist die Lautstärke dann ohnehin schon 0 → .muted redundant).
      this._muteDelay = setTimeout(function () { if (self.audio) self.audio.muted = true; }, 200);
      log('Muted');
    } else {
      // Unmute SYNCHRON in der Klick-Geste (iOS spielt dann sofort hörbar
      // weiter), danach der sanfte Volume-Fade fürs Desktop.
      if (this.audio) this.audio.muted = false;
      var vol = this.isDucked ? this.duckVolume : this.baseVolume;
      this._fadeTo(vol, 200);
      log('Unmuted, vol:', vol);
    }

    // AP-B/B3 (2026-07-23): Der Ton-Knopf ist die ZENTRALE — er stummt
    // nicht nur die Atmo, sondern über den MediaManager auch O-Ton,
    // Bubbles, Galerie-Audio und Pano-Hotspots (die alte nb-Mute-Lücke
    // wird bewusst nicht geerbt, AP-C).
    if (window.StoryMediaManager && typeof window.StoryMediaManager.setMuted === 'function') {
      window.StoryMediaManager.setMuted(this.isMuted);
    }

    this._updateToggleUI();
  };

  /* ========================================
     Fade Helper
     ======================================== */

  StoryAmbientAudio.prototype._fadeTo = function (targetVol, durationMs) {
    if (!this.audio) return;

    clearInterval(this._fadeInterval);

    var steps = Math.max(1, Math.round(durationMs / 50));
    var currentVol = this.audio.volume;
    var delta = (targetVol - currentVol) / steps;
    var step = 0;
    var self = this;

    this._fadeInterval = setInterval(function () {
      step++;
      if (step >= steps) {
        self.audio.volume = Math.max(0, Math.min(1, targetVol));
        clearInterval(self._fadeInterval);
        self._fadeInterval = null;
      } else {
        self.audio.volume = Math.max(0, Math.min(1, currentVol + delta * step));
      }
    }, 50);
  };

  /* ========================================
     Toggle Button UI
     ======================================== */

  StoryAmbientAudio.prototype._createToggleUI = function () {
    // Create button
    var btn = document.createElement('button');
    btn.className = 'ambient-toggle';
    var ambientLabel = (/^\/en(\/|$)/.test(window.location.pathname) || document.documentElement.lang === 'en') ? 'Background sound on/off' : 'Hintergrundmusik ein/aus';
    btn.setAttribute('aria-label', ambientLabel);
    btn.setAttribute('title', ambientLabel);
    btn.innerHTML = this._speakerIcon(true);
    btn.style.display = 'none'; // Hidden until playback starts

    var self = this;
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      self.toggleMute();
    });

    document.body.appendChild(btn);
    this.toggleBtn = btn;
  };

  StoryAmbientAudio.prototype._showToggle = function () {
    if (this.toggleBtn) {
      this.toggleBtn.style.display = '';
    }
  };

  StoryAmbientAudio.prototype._updateToggleUI = function () {
    if (!this.toggleBtn) return;
    this.toggleBtn.innerHTML = this._speakerIcon(!this.isMuted);
    this.toggleBtn.classList.toggle('is-muted', this.isMuted);
  };

  StoryAmbientAudio.prototype._speakerIcon = function (on) {
    if (on) {
      // Speaker with waves
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
        '<polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>' +
        '<path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>' +
        '<path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>' +
        '</svg>';
    } else {
      // Speaker with X (muted)
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
        '<polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>' +
        '<line x1="23" y1="9" x2="17" y2="15"/>' +
        '<line x1="17" y1="9" x2="23" y2="15"/>' +
        '</svg>';
    }
  };

  /* ========================================
     Cleanup
     ======================================== */

  StoryAmbientAudio.prototype.destroy = function () {
    clearInterval(this._fadeInterval);
    clearTimeout(this._muteDelay);
    if (this.audio) {
      this.audio.pause();
      this.audio.src = '';
      this.audio = null;
    }
    if (this.toggleBtn && this.toggleBtn.parentNode) {
      this.toggleBtn.parentNode.removeChild(this.toggleBtn);
    }
    this.isPlaying = false;
    this.started = false;
  };

  /* ========================================
     Expose
     ======================================== */

  window.StoryModuleAmbient = StoryAmbientAudio;

})(window);
